import { createSlice } from '@reduxjs/toolkit'
import { actions as dashboardFieldsActionsF } from 'DashboardBuilder/store/dashboardFields'
import { actions as questionsActionsF } from 'DashboardBuilder/store/questions'
import { actions as answersActionsF } from 'DashboardBuilder/store/answers'
import { actions as dashboardActionsF } from 'DashboardBuilder/store/dashboard'

const slice = createSlice({
  name: 'builder',
  initialState: {
    dashboardId: null,
    loaded: false,
    mergeTags: [],
    currentEditor: null
  },
  reducers: {
    set: (s, a) => a.payload,
    upsert: (s, a) => ({ ...s, ...a.payload })
  },
})

const { reducer } = slice

const actions = (dis, store, restClient) => {
  const answersActions = answersActionsF(dis, store, restClient)
  const dashboardActions = dashboardActionsF(dis, store, restClient)
  const dashboardFieldsActions = dashboardFieldsActionsF(dis, store, restClient)
  const questionsActions = questionsActionsF(dis, store, restClient)

  function load(dashboardId) {
    dis(slice.actions.upsert({ dashboardId, loaded: false }))

    return restClient.get(`/api/dashboards/${dashboardId}`)
      .then(({ data }) => {
        setSlicesData(data)
      })
      .catch((error) => {
        console.error('Error loading dashboard:', error)
        dis(slice.actions.upsert({ loaded: false }))
      })
  }

  function setSlicesData(data) {
    const { dashboardFields, survey } = data
    const questions = survey.questions.map(q => _.omit(q, 'answers'))
    const answers = _.flatten(questions.map(q => q.answers)).filter(Boolean)
    const dashboard = _.omit(data, 'dashboardFields', 'survey')

    dashboardFieldsActions.setAll(dashboardFields)
    questionsActions.setAll(questions)
    answersActions.setAll(answers)
    dashboardActions.set(dashboard)

    dis(slice.actions.upsert({ loaded: true }))
  }

  function loadMergeTags() {
    // create a new axios client which bypasses the case conversion - we want to
    // load merge tags in snake case
    return axios
      .create()
      .get(`/api/render/available_merge_tags`)
      .then(({ data: mergeTags }) => {
        dis(slice.actions.upsert({ mergeTags }))
      })
  }

  function save(step) {
    const state = store.getState()
    const dashboardId = state.builder.dashboardId
    
    let payload = {}
    let extraData = {}
    
    if (step === 'audience') {
      const dashboard = state.dashboard
      const { searchId, autoRefreshSearch } = dashboard
      payload = { searchId, autoRefreshSearch }
    } 
    else if (step === 'instructions') {
      const { instructions } = state.dashboard
      payload = { instructions }
    }
    else if (step === 'settings') {
      const { settings } = state.dashboard
      payload = { settings }
    }
    else if (step === 'dashboardFields') {
      const dashboardFields = _.values(state.dashboardFields.entities)
      payload = { dashboardFields }

      const questions = _.values(state.questions.entities)
      const answers = _.values(state.answers.entities)
      extraData = { survey: { questions, answers } }
    } else {
      throw new Error(`Unknown step: ${step}`)
    }
    
    return restClient.put(`/api/dashboards/${dashboardId}`, { dashboard: payload, ...extraData })
      .then(({ data }) => {
        // Update both slices to keep them in sync
        setSlicesData(data)
        return data
      })
      .catch((error) => {
        console.error('Error saving dashboard:', error)
      })
  }

  function closeEditor() {
    return dis({ type: 'builder/upsert', payload: { currentEditor: null } })
  }

  function openEditor(editor) {
    return dis({ type: 'builder/upsert', payload: { currentEditor: editor } })
  }

  function launch() {
    const state = store.getState()
    const dashboardId = state.builder.dashboardId

    return restClient.post(`/api/dashboards/${dashboardId}/launch`)
      .then(({ data }) => {
        return data
      })
      .catch((error) => {
        console.error('Error launching dashboard:', error)
      })
  }

  return {
    load,
    save,
    closeEditor,
    openEditor,
    loadMergeTags,
    launch
  }
}

export default { reducer, actions }
